import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import TopicCard from "@/components/feature/TopicCard";
import { topicList } from "@/mocks/topics";
import { categoryList } from "@/mocks/categories";
import type { Category, Topic } from "@/types/content";

type Period = "today" | "week" | "month";

const PERIODS: { key: Period; label: string; note: string }[] = [
  { key: "today", label: "今日榜", note: "反映短时间内的爆发讨论，变化最快" },
  { key: "week", label: "本周榜", note: "更看重持续性，能过滤一日游话题" },
  { key: "month", label: "本月榜", note: "体现长期热度，适合回顾整月趋势" },
];

export default function Rank() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<Period>("today");

  const topics: Topic[] = topicList;
  const categories: Category[] = categoryList;

  const activeCategory = searchParams.get("category") ?? "all";
  const query = searchParams.get("q") ?? "";

  const ranked = useMemo(() => {
    const list = topics
      .filter((t) => (activeCategory === "all" ? true : t.categorySlug === activeCategory))
      .filter((t) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (
          t.title.toLowerCase().includes(q) ||
          t.summary.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q) ||
          t.tags.some((tag) => tag.toLowerCase().includes(q))
        );
      });

    return [...list].sort((a, b) => {
      if (period === "week") return a.weeklyRank - b.weeklyRank;
      if (period === "month") return a.monthlyRank - b.monthlyRank;
      return a.rank - b.rank;
    });
  }, [topics, activeCategory, query, period]);

  const setCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") next.delete("category");
    else next.set("category", slug);
    setSearchParams(next, { replace: true });
  };

  const clearQuery = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const activePeriodMeta = PERIODS.find((p) => p.key === period)!;
  const activeCategoryName =
    activeCategory === "all"
      ? "全部栏目"
      : (categories.find((c) => c.slug === activeCategory)?.name ?? "全部栏目");

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "网络爆料热议榜单",
      url: "https://sunshinelabs.cn/rank",
      description: "网络爆料今日、本周、本月热议榜单，支持按栏目筛选与关键词搜索。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://sunshinelabs.cn/" },
        { "@type": "ListItem", position: 2, name: "热议榜单", item: "https://sunshinelabs.cn/rank" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${activePeriodMeta.label} · ${activeCategoryName}`,
      itemListElement: ranked.slice(0, 10).map((t, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://sunshinelabs.cn/topic/${t.id}`,
        name: t.title,
      })),
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="网络爆料热议榜单 - 今日/本周/本月排行 | 瓜点"
        description="网络爆料榜单实时排行，提供今日榜、本周榜、本月榜切换，支持按娱乐、科技、影视、游戏等栏目筛选，快速查看最新热议事件名次。"
        keywords="网络爆料榜单,网络爆料热议,网络爆料最新,网络爆料今日,话题榜,热度排行"
        path="/rank"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "热议榜单" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            网络爆料热议榜单
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            今日榜看爆发，本周榜看持续，本月榜看长期。切换周期与栏目，快速定位当前最值得关注的爆料话题与名次变化。
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div
              className="flex w-fit items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1"
              role="tablist"
              aria-label="切换榜单周期"
            >
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  role="tab"
                  aria-selected={period === p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    period === p.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-foreground-500">{activePeriodMeta.note}</p>
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setCategory("all")}
              className={`whitespace-nowrap rounded-full px-3.5 py-2 text-sm transition-colors ${
                activeCategory === "all"
                  ? "bg-secondary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-secondary-300"
              }`}
            >
              全部栏目
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setCategory(c.slug)}
                className={`whitespace-nowrap rounded-full px-3.5 py-2 text-sm transition-colors ${
                  activeCategory === c.slug
                    ? "bg-secondary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-secondary-300"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {query && (
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
              <span className="flex items-center gap-1.5">
                <i className="ri-search-line"></i>
                搜索关键词：<strong className="font-medium">{query}</strong>
              </span>
              <button
                type="button"
                onClick={clearQuery}
                className="whitespace-nowrap text-xs underline underline-offset-4"
              >
                清除
              </button>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between border-b border-background-200 pb-3 text-xs text-foreground-500">
            <span>
              共找到 <strong className="text-foreground-900">{ranked.length}</strong> 条爆料
            </span>
            <span>榜单每小时刷新一次</span>
          </div>

          {ranked.length > 0 ? (
            <>
              <ol className="mt-2 divide-y divide-background-200">
                {ranked.map((topic, idx) => (
                  <li key={topic.id}>
                    <Link
                      to={`/topic/${topic.id}`}
                      className="group grid grid-cols-[auto_1fr] items-start gap-4 py-5 transition-colors md:grid-cols-[auto_1fr_auto]"
                    >
                      <span
                        className={`flex h-11 w-11 items-center justify-center rounded-md font-heading text-lg font-bold ${
                          idx === 0
                            ? "bg-primary-500 text-background-50"
                            : idx === 1
                              ? "bg-accent-500 text-accent-950"
                              : idx === 2
                                ? "bg-secondary-500 text-background-50"
                                : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="rounded-full bg-primary-100 px-2.5 py-1 text-primary-700">
                            {topic.category}
                          </span>
                          <span className="text-foreground-500">{topic.publishTime}</span>
                        </span>
                        <span className="mt-2 block font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                          {topic.title}
                        </span>
                        <span className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                          {topic.summary}
                        </span>
                      </span>
                      <span className="col-span-2 flex items-center gap-4 text-xs text-foreground-500 md:col-span-1 md:flex-col md:items-end md:gap-2">
                        <span className="flex items-center gap-1 text-sm font-medium text-primary-600">
                          <i className="ri-fire-fill"></i>
                          {topic.heat.toLocaleString("zh-CN")}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line"></i>
                          {topic.comments}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>

              <div className="mt-12">
                <h2 className="font-heading text-xl font-bold text-foreground-950">
                  榜单精选 · 图文速览
                </h2>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {ranked.slice(0, 3).map((topic) => (
                    <TopicCard key={topic.id} topic={topic} />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 p-10 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-xl"></i>
              </span>
              <p className="mt-4 text-sm text-foreground-600">
                没有找到与当前条件匹配的爆料，试试更换栏目或清除关键词。
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchParams({}, { replace: true });
                }}
                className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}